import { ExternalLinkIcon, WarningIcon } from '@chakra-ui/icons';
import { Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';

interface AthenaDepartmentLinkProps {
  departmentName?: string;
  departmentLink?: string;
  practiceName?: string;
  practiceLocation?: string;
  practiceAddress?: string;
  practiceId?: string;
}

const AthenaDepartmentLink: React.FC<AthenaDepartmentLinkProps> = ({
  departmentName,
  departmentLink,
  practiceName,
  practiceLocation,
  practiceAddress,
  practiceId,
}) => {
  const navigate = useNavigate();
  const handleLinkClick = () => {
    if (!departmentLink) return;

    // Ensure the URL has a proper protocol
    let normalizedUrl = departmentLink;
    if (
      !departmentLink.startsWith('http://') &&
      !departmentLink.startsWith('https://')
    ) {
      normalizedUrl = `https://${departmentLink}`;
    }

    window.open(normalizedUrl, '_blank');
  };

  const hasValidDepartment =
    departmentName && departmentName.trim() !== '';
  const hasValidLink = departmentLink && departmentLink.trim() !== '';

  // Build practice label (name + location)
  const practiceLabel = React.useMemo(() => {
    if (!practiceName) return null;

    if (practiceLocation) {
      return `${practiceName} - ${practiceLocation}`;
    }
    return practiceName;
  }, [practiceName, practiceLocation]);

  return (
    <Flex
      gap="0.375rem"
      alignItems="flex-start"
      flexWrap="wrap"
      padding="0.125rem 0.25rem"
      borderRadius="0.375rem"
      transition="background-color 120ms ease-in-out"
      _hover={{ background: 'gray.50' }}
      minHeight="auto"
    >
      {/* Practice Name/Location + Address */}
      {practiceLabel ? (
        <Flex direction="column" minW="12rem" gap="0.0625rem">
          <Text
            role={practiceId ? 'button' : undefined}
            tabIndex={practiceId ? 0 : -1}
            fontSize="0.75rem"
            fontWeight="500"
            color="gray.700"
            lineHeight="0.875rem"
            cursor={practiceId ? 'pointer' : 'default'}
            _hover={{
              color: practiceId ? 'blue.600' : 'gray.700',
              textDecoration: practiceId ? 'underline' : 'none',
            }}
            onClick={() => {
              if (practiceId) navigate(`/practices/${practiceId}`);
            }}
            onKeyDown={(e) => {
              if (!practiceId) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/practices/${practiceId}`);
              }
            }}
          >
            {practiceLabel}
          </Text>
          {practiceAddress && (
            <Text
              fontSize="0.6875rem"
              color="gray.500"
              lineHeight="0.75rem"
            >
              {practiceAddress}
            </Text>
          )}
        </Flex>
      ) : (
        <Flex direction="column" minW="12rem" gap="0.0625rem">
          <Text
            fontSize="0.75rem"
            fontWeight="400"
            color="gray.400"
            lineHeight="0.875rem"
            fontStyle="italic"
          >
            Practice information unavailable
          </Text>
        </Flex>
      )}

      {/* Tablespace Badge */}
      <CustomFieldLabel
        customField="Tablespace"
        fieldValue={hasValidDepartment ? departmentName : 'Not Set'}
        fieldValueBgColor={
          hasValidDepartment ? 'background.green100' : 'gray.100'
        }
        enableTruncate={false}
        hideCustomFieldLabel={true}
      />

      {/* Link/Warning Icon */}
      {hasValidLink ? (
        <Tooltip label={`URL: ${departmentLink} (Opens in new tab)`}>
          <IconButton
            onClick={handleLinkClick}
            aria-label="Open Athena Department Link"
            icon={<ExternalLinkIcon />}
            background="none"
            size="sm"
          />
        </Tooltip>
      ) : (
        <Tooltip label="Athena Department information not available. You can add this via the Edit Provider form.">
          <IconButton
            aria-label="Athena Department Link not found"
            icon={<WarningIcon />}
            background="none"
            size="sm"
            isDisabled
            opacity={0.6}
            cursor="not-allowed"
          />
        </Tooltip>
      )}
    </Flex>
  );
};

export default AthenaDepartmentLink;
